'use client';

import { Suspense, lazy } from 'react';
import { HeroSkeleton } from './home/LoadingState';
import { ErrorBoundary } from './ErrorBoundary';
import ClientOnly from "@/components/ClientOnly";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Leaf, Mail, Beaker, ShieldCheck, TrendingUp, Users, Bookmark, HelpCircle, ShoppingCart, BookOpen, Receipt, ShoppingBag, Package } from "lucide-react";
import { AnimatedSection } from "./home/AnimatedSection";
import { useState, useEffect } from 'react';

// Static imports for consistent rendering
import industryImage from '../../../public/images/company/image-101.webp';
import logoWhite from '../../../public/logo-white.svg';

// Import FAQ data
import { faqItems } from '../empresa/components/faq/faqData';

// Get featured FAQs for homepage (show 6 most relevant)
const homepageFaqs = faqItems.slice(0, 6);

// Main navigation sections
const mainSections = [
  {
    title: "Tienda",
    description: "Explore nuestro catálogo completo de productos químicos industriales.",
    icon: <ShoppingCart className="h-6 w-6" />,
    link: "/productos",
    color: "bg-blue-50 text-blue-600"
  },
  {
    title: "Cotizar",
    description: "Solicite presupuestos personalizados para sus necesidades específicas.",
    icon: <ArrowRight className="h-6 w-6" />,
    link: "/cotizar",
    color: "bg-green-50 text-green-600"
  },
  {
    title: "Revendedores",
    description: "Conozca nuestro programa para distribuidores y revendedores.",
    icon: <TrendingUp className="h-6 w-6" />,
    link: "/revendedores",
    color: "bg-amber-50 text-amber-600"
  },
  {
    title: "Empresa",
    description: "Conozca nuestra historia, misión y valores corporativos.",
    icon: <Users className="h-6 w-6" />,
    link: "/empresa",
    color: "bg-indigo-50 text-indigo-600"
  },
  {
    title: "Calidad",
    description: "Descubra nuestros estándares de calidad y compromiso.",
    icon: <ShieldCheck className="h-6 w-6" />,
    link: "/calidad",
    color: "bg-purple-50 text-purple-600"
  },
  {
    title: "Precursores",
    description: "Información sobre nuestra política de precursores químicos.",
    icon: <Bookmark className="h-6 w-6" />,
    link: "/precursores",
    color: "bg-rose-50 text-rose-600"
  },
  {
    title: "Blog",
    description: "Manténgase actualizado con noticias y tendencias de la industria química.",
    icon: <BookOpen className="h-6 w-6" />,
    link: "/blog",
    color: "bg-emerald-50 text-emerald-600"
  },
  {
    title: "Contacto",
    description: "Comuníquese con nuestro equipo de atención al cliente.",
    icon: <Mail className="h-6 w-6" />,
    link: "/contacto",
    color: "bg-cyan-50 text-cyan-600"
  },
  {
    title: "Preguntas frecuentes",
    description: "Encuentre respuestas a las consultas más comunes.",
    icon: <HelpCircle className="h-6 w-6" />,
    link: "/empresa/faq",
    color: "bg-teal-50 text-teal-600"
  },
  {
    title: "Legajo Impositivo",
    description: "Acceda a nuestra documentación tributaria y fiscal oficial.",
    icon: <Receipt className="h-6 w-6" />,
    link: "/informacion-tributaria",
    color: "bg-gray-50 text-gray-600"
  }
];

// Lazy load components
const Testimonials = lazy(() => import('./home/Testimonials'));
// const TrustIndicators = lazy(() => import('./home/TrustIndicators'));

export default function HomeClient() {
  const [isLoading, setIsLoading] = useState(true);
  
  // After mount, set loading to false
  useEffect(() => {
    setIsLoading(false);
  }, []);
  
  // Skeleton loading state for the server render
  const loadingFallback = (
    <div className="min-h-screen animate-pulse">
      <div className="bg-blue-900/50 py-24">
        <div className="container mx-auto px-4 text-center">
          <div className="h-16 bg-white/20 rounded mb-8"></div>
          <div className="h-8 bg-white/20 rounded w-2/3 mx-auto mb-8"></div>
          <div className="flex justify-center space-x-4">
            <div className="h-12 w-32 bg-white/20 rounded"></div>
            <div className="h-12 w-32 bg-white/20 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
  
  return (
    <ClientOnly fallback={loadingFallback}>
      <main className="min-h-screen">
        <ErrorBoundary>
          {/* Enhanced Hero Section */}
          <Suspense fallback={<HeroSkeleton />}>
            <section className="relative bg-gradient-to-br from-red-700 via-red-800 to-red-950 py-6 md:py-8 pb-12 md:pb-16 overflow-hidden min-h-[85vh] md:min-h-[45vmax] flex items-center">
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-[conic-gradient(at_top_right,rgb(153,27,27),rgb(220,38,38),rgb(127,29,29))]"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-red-950/80 via-transparent to-transparent"></div>
                <div className="absolute inset-0 bg-cover bg-center opacity-5 mix-blend-overlay mt-0"></div>
              </div>
              <div 
                className="container-page relative z-1 flex flex-col items-center px-4 md:px-6"
              >
                <AnimatedSection
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center w-full max-w-4xl mx-auto"
                >
                  <div className="inline-block mb-8 md:mb-10 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/10 backdrop-blur-sm">
                    <p className="text-white/90 text-xs md:text-base">
                      Más de 40 años de experiencia en la industria química
                    </p>
                  </div>
                  <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-6 mb-8 md:mb-10">
                    <div className="flex-shrink-0">
                      <AnimatedSection
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                      >
                        <Image
                          src={logoWhite}
                          alt="Droquimar"
                          width={180}
                          height={120}
                          className="w-[135px] h-auto object-contain"
                          priority
                        />
                      </AnimatedSection>
                    </div>
                    <div className="flex-shrink-0">
                      <AnimatedSection
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        className="w-full"
                      >
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="inline-block mb-8">
                            <div className="relative">
                              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight inline-block">
                                Droquimar
                              </h1>
                              <div className="absolute bottom-[-1.7rem] md:bottom-[-2rem] lg:bottom-[-2.4rem]">
                                <h2 className="text-[12px] md:text-sm lg:text-base text-white/90 tracking-wider uppercase pt-1.5 whitespace-nowrap">
                                  Productos Químicos Industriales
                                </h2>
                              </div>
                            </div>
                          </div>
                        </div>
                      </AnimatedSection>
                    </div>
                  </div>
                  <p className="text-lg md:text-xl lg:text-2xl mb-8 md:mb-10 text-gray-100 max-w-3xl mx-auto leading-relaxed px-4">
                    Importación, fraccionamiento y comercialización de productos químicos para industrias desde 1982.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
                    <Link
                      href="/cotizar"
                      className="btn-secondary inline-flex items-center justify-center gap-2 text-base md:text-lg px-6 md:px-8 py-3 md:py-4 rounded-md hover:scale-105 transition-transform bg-white text-brand-primary hover:bg-blue-50 w-full sm:w-auto"
                    >
                      Cotizar ahora <ArrowRight size={18} className="md:w-5 md:h-5" />
                    </Link>
                    <Link
                      href="/contacto"
                      className="bg-white/10 text-white backdrop-blur-sm inline-flex items-center justify-center gap-2 text-base md:text-lg px-6 md:px-8 py-3 md:py-4 rounded-md hover:bg-white/20 transition-all border border-white/20 w-full sm:w-auto"
                    >
                      Contactar <Mail size={18} className="md:w-5 md:h-5" />
                    </Link>
                  </div>
                  <div className="mt-8 md:mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto px-4">
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-1 md:mb-2">+1000</div>
                      <div className="text-xs md:text-sm text-gray-200">Productos</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-1 md:mb-2">+500</div>
                      <div className="text-xs md:text-sm text-gray-200">Clientes Activos</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-1 md:mb-2">40+</div>
                      <div className="text-xs md:text-sm text-gray-200">Años de Experiencia</div>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
              <div className="absolute bottom-0 left-0 right-0 translate-y-40 hidden min-[1900px]:block">
                <svg className="fill-gray-50 w-full block h-90" viewBox="0 0 1440 320" preserveAspectRatio="none">
                  <path d="M0,160L48,170.7C96,181,192,203,288,192C384,181,480,139,576,133.3C672,128,768,160,864,160C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L0,320Z"></path>
                </svg>
              </div>
            </section>
          </Suspense>

          {/* Quick Actions Section */}
          {/* Note: Tienda card removed — online store hidden until further notice.
              Cotizar card promoted to full-width hero. */}
          <ErrorBoundary>
            <section className="py-12 md:py-16 bg-gradient-to-b from-gray-50 to-white">
              <div className="container-page max-w-5xl mx-auto">
                <AnimatedSection
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all"
                >
                  <Link href="/cotizar" className="block h-full">
                    <div className="relative h-64 md:h-80 bg-gradient-to-br from-green-500 to-emerald-600 p-8 flex flex-col justify-between">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
                      <div className="relative z-10">
                        <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-2xl flex items-center justify-center mb-4">
                          <ShoppingBag className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">Cotizar</h3>
                        <p className="text-white/90 text-lg">
                          Solicite presupuestos personalizados para sus necesidades específicas
                        </p>
                      </div>
                      <div className="relative z-10 flex items-center text-white font-semibold group-hover:translate-x-2 transition-transform">
                        Comenzar <ArrowRight className="ml-2 h-5 w-5" />
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              </div>
            </section>
          </ErrorBoundary>

          {/* Product Categories Section */}
          <ErrorBoundary>
            <section className="py-12 md:py-24 bg-white">
              <div className="container-page max-w-7xl mx-auto">
                <div className="text-center mb-12 md:mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Nuestros Productos</h2>
                  <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                    Soluciones especializadas para diferentes industrias y aplicaciones
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <AnimatedSection
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <Link href="/cotizar?category=Productos+Químicos" className="block">
                      <div className="relative h-64">
                        <Image
                          src="/images/institution/Productos-quimicos.webp"
                          alt="Productos Químicos"
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-xl font-bold text-white mb-2">Productos Químicos</h3>
                          <p className="text-white/90 text-sm mb-3">Materias primas para industria</p>
                          <div className="flex items-center text-white font-medium group-hover:translate-x-1 transition-transform">
                            Ver más <ArrowRight className="ml-1 h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>

                  <AnimatedSection
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <Link href="/cotizar?category=Limpieza+Hogar+y+Autos" className="block">
                      <div className="relative h-64">
                        <Image
                          src="/images/institution/Productos-de-limpieza-hogar-e-industria.webp"
                          alt="Productos de Limpieza"
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-xl font-bold text-white mb-2">Limpieza Hogar e Industria</h3>
                          <p className="text-white/90 text-sm mb-3">Soluciones profesionales</p>
                          <div className="flex items-center text-white font-medium group-hover:translate-x-1 transition-transform">
                            Ver más <ArrowRight className="ml-1 h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>

                  <AnimatedSection
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <Link href="/cotizar?category=Piletas+de+Natación" className="block">
                      <div className="relative h-64">
                        <Image
                          src="/images/institution/Productos-de-limpieza-piletas-y-piscinas.webp"
                          alt="Productos para Piletas"
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-xl font-bold text-white mb-2">Piletas y Piscinas</h3>
                          <p className="text-white/90 text-sm mb-3">Mantenimiento de piscinas</p>
                          <div className="flex items-center text-white font-medium group-hover:translate-x-1 transition-transform">
                            Ver más <ArrowRight className="ml-1 h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>

                  <AnimatedSection
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <Link href="/cotizar?category=Limpieza+Hogar+y+Autos" className="block">
                      <div className="relative h-64">
                        <Image
                          src="/images/institution/Productos-de-limpieza-automovil.webp"
                          alt="Productos Automotor"
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-xl font-bold text-white mb-2">Productos Automotor</h3>
                          <p className="text-white/90 text-sm mb-3">Cuidado de vehículos</p>
                          <div className="flex items-center text-white font-medium group-hover:translate-x-1 transition-transform">
                            Ver más <ArrowRight className="ml-1 h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                </div>
              </div>
            </section>
          </ErrorBoundary>

          {/* Company & Quality Section */}
          <ErrorBoundary>
            <section className="py-12 md:py-24 bg-gray-50">
              <div className="container-page max-w-7xl mx-auto">
                <div className="text-center mb-12 md:mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Calidad y Compromiso</h2>
                  <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                    Controles de calidad y políticas que respaldan nuestro trabajo
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                  <AnimatedSection
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all bg-white"
                  >
                    <Link href="/politicas/calidad" className="block">
                      <div className="relative h-48 overflow-hidden">
                        <Image
                          src="/images/institution/Politica-calidad.webp"
                          alt="Política de Calidad"
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center mb-3">
                          <div className="bg-blue-100 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                            <ShieldCheck className="h-5 w-5 text-blue-600" />
                          </div>
                          <h3 className="text-xl font-bold">Política de Calidad</h3>
                        </div>
                        <p className="text-gray-700 mb-4">
                          Controles rigurosos para garantizar la excelencia en todos nuestros productos
                        </p>
                        <div className="text-brand-primary font-medium inline-flex items-center group-hover:translate-x-1 transition-transform">
                          Ver política <ArrowRight className="ml-1 h-4 w-4" />
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>

                  <AnimatedSection
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all bg-white"
                  >
                    <Link href="/politicas/ambiental" className="block">
                      <div className="relative h-48 overflow-hidden">
                        <Image
                          src="/images/institution/Laboratorio.webp"
                          alt="Política Ambiental"
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center mb-3">
                          <div className="bg-green-100 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                            <Leaf className="h-5 w-5 text-green-600" />
                          </div>
                          <h3 className="text-xl font-bold">Política Ambiental</h3>
                        </div>
                        <p className="text-gray-700 mb-4">
                          Compromiso con la protección del medio ambiente y prácticas sostenibles
                        </p>
                        <div className="text-brand-primary font-medium inline-flex items-center group-hover:translate-x-1 transition-transform">
                          Ver política <ArrowRight className="ml-1 h-4 w-4" />
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>

                  <AnimatedSection
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all bg-white"
                  >
                    <Link href="/politicas/precursores-quimicos" className="block">
                      <div className="relative h-48 overflow-hidden">
                        <Image
                          src="/images/institution/Politica-precursores-quimicos.webp"
                          alt="Política de Precursores Químicos"
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center mb-3">
                          <div className="bg-amber-100 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                            <Bookmark className="h-5 w-5 text-amber-600" />
                          </div>
                          <h3 className="text-xl font-bold">Precursores Químicos</h3>
                        </div>
                        <p className="text-gray-700 mb-4">
                          Cumplimiento normativo y manejo responsable de sustancias controladas
                        </p>
                        <div className="text-brand-primary font-medium inline-flex items-center group-hover:translate-x-1 transition-transform">
                          Ver política <ArrowRight className="ml-1 h-4 w-4" />
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                </div>
              </div>
            </section>
          </ErrorBoundary>
          
          {/* About Summary */}
          <ErrorBoundary>
            <section className="py-12 md:py-24 bg-white relative overflow-hidden">
              <div className="container-page max-w-7xl mx-auto relative flex flex-col md:flex-row gap-6 md:gap-12">
                <div className="md:w-1/2 relative mb-8 md:mb-0">
                  <AnimatedSection
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="h-[400px] w-full relative overflow-hidden rounded-xl shadow-xl"
                  >
                    <Image
                      src={industryImage}
                      alt="Nuestras instalaciones"
                      fill
                      quality={90}
                      className="object-cover"
                    />
                  </AnimatedSection>
                </div>
                
                <AnimatedSection
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="md:w-1/2"
                >
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6">
                    Compromiso con la calidad
                  </h2>
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Importamos, fraccionamos y comercializamos productos químicos; y fabricamos productos de limpieza y de mantenimiento industrial con el mayor compromiso con la calidad.
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700 mb-8">
                    Promovemos la mejora continua y la gestión sustentable en todos nuestros procesos, asegurando resultados óptimos para nuestros clientes.
                  </p>
                  <Link href="/empresa" className="inline-block bg-brand-primary text-white px-8 py-3 rounded-md hover:bg-blue-700 transition-colors">
                    Conocer más sobre nosotros
                  </Link>
                </AnimatedSection>
              </div>
            </section>
          </ErrorBoundary>
          
          {/* Client Reviews */}
          <ErrorBoundary>
            <Suspense fallback={<div className="py-16 animate-pulse">
              <div className="container-page max-w-7xl mx-auto">
                <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-6 shadow-lg">
                      <div className="mb-6">
                        <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                      </div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>}>
              <Testimonials />
            </Suspense>
          </ErrorBoundary>
          
          {/* Revendedores */}
          <ErrorBoundary>
            <section className="py-12 md:py-24 bg-white relative overflow-hidden">
              <div className="container-page max-w-7xl mx-auto">
                <AnimatedSection
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="relative overflow-hidden rounded-2xl shadow-2xl"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="relative h-64 lg:h-auto min-h-[300px]">
                      <Image
                        src="/images/institution/Programa-revendedores.webp"
                        alt="Programa de Revendedores"
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="bg-gradient-to-br from-brand-primary to-blue-700 p-8 md:p-12 flex flex-col justify-center text-white">
                      <div className="mb-4">
                        <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full inline-block">
                          <p className="text-sm font-medium whitespace-nowrap">Oportunidad de Negocio</p>
                        </div>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold mb-4">¿Quiere ser revendedor?</h2>
                      <p className="text-lg md:text-xl mb-6 text-white/90">
                        Únase a nuestra red de distribuidores y acceda a beneficios exclusivos
                      </p>
                      <ul className="space-y-3 mb-8">
                        <li className="flex items-start">
                          <ArrowRight className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                          <span>Precios mayoristas especiales</span>
                        </li>
                        <li className="flex items-start">
                          <ArrowRight className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                          <span>Soporte técnico y capacitación continua</span>
                        </li>
                        <li className="flex items-start">
                          <ArrowRight className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                          <span>Material de marketing y promoción</span>
                        </li>
                      </ul>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Link href="/revendedores" className="inline-flex items-center justify-center gap-2 bg-white text-brand-primary px-6 py-3 rounded-md hover:bg-gray-100 transition-colors font-semibold">
                          Conocer más <TrendingUp size={18} />
                        </Link>
                        <Link href="/contacto" className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white px-6 py-3 rounded-md hover:bg-white/20 transition-colors font-semibold">
                          Contactar <Mail size={18} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
            </section>
          </ErrorBoundary>
          
          {/* Blog & Resources Section */}
          <ErrorBoundary>
            <section className="py-12 md:py-24 bg-gray-50">
              <div className="container-page max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                  {/* Blog Preview */}
                  <AnimatedSection
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-2xl p-8 shadow-lg"
                  >
                    <div className="flex items-center mb-6">
                      <div className="bg-emerald-100 w-12 h-12 rounded-xl flex items-center justify-center mr-4">
                        <BookOpen className="h-6 w-6 text-emerald-600" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold">Blog y Novedades</h2>
                    </div>
                    <p className="text-gray-700 text-lg mb-6">
                      Manténgase actualizado con las últimas noticias, tendencias de la industria química y consejos de expertos.
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Artículos técnicos y guías de uso</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Novedades en productos químicos</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Consejos de seguridad y manipulación</span>
                      </li>
                    </ul>
                    <Link href="/blog" className="inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-md hover:bg-emerald-700 transition-colors font-semibold">
                      Visitar blog <ArrowRight className="h-5 w-5" />
                    </Link>
                  </AnimatedSection>

                  {/* Contact & Support */}
                  <AnimatedSection
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-2xl p-8 shadow-lg"
                  >
                    <div className="flex items-center mb-6">
                      <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center mr-4">
                        <Mail className="h-6 w-6 text-blue-600" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold">Contacto y Soporte</h2>
                    </div>
                    <p className="text-gray-700 text-lg mb-6">
                      Nuestro equipo de expertos está listo para ayudarle con asesoramiento técnico personalizado.
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Atención personalizada</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Asesoramiento técnico especializado</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Respuesta rápida a consultas</span>
                      </li>
                    </ul>
                    <Link href="/contacto" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-semibold">
                      Contactar ahora <ArrowRight className="h-5 w-5" />
                    </Link>
                  </AnimatedSection>
                </div>
              </div>
            </section>
          </ErrorBoundary>

          {/* FAQ */}
          <ErrorBoundary>
            <section className="py-12 md:py-24 bg-white">
              <div className="container-page max-w-7xl mx-auto">
                <div className="text-center mb-12 md:mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Preguntas Frecuentes</h2>
                  <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                    Encuentre respuestas rápidas a las consultas más comunes
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                  {homepageFaqs.map((faq, index) => (
                    <AnimatedSection
                      key={faq.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow"
                    >
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="bg-brand-primary/10 w-10 h-10 rounded-lg flex items-center justify-center">
                            <HelpCircle className="h-5 w-5 text-brand-primary" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold mb-2 text-gray-900">{faq.question}</h3>
                          <p className="text-gray-700 text-sm leading-relaxed">
                            {faq.answer.split('\n')[0]}
                          </p>
                        </div>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>

                <div className="text-center mt-12">
                  <Link href="/empresa/faq" className="text-brand-primary font-semibold inline-flex items-center gap-2 group hover:gap-3 transition-all">
                    Ver todas las preguntas frecuentes
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              </div>
            </section>
          </ErrorBoundary>
        </ErrorBoundary>
      </main>
    </ClientOnly>
  );
}

// Main section card component
function MainSectionCard({ title, description, icon, link, color, delay = 0 }: {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  color: string;
  delay?: number;
}) {
  return (
    <AnimatedSection
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group"
    >
      <Link href={link} className="block h-full">
        <div className="p-6">
          <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center mb-4`}>
            {icon}
          </div>
          <h3 className="text-xl font-semibold mb-2">{title}</h3>
          <p className="text-gray-700 mb-4">{description}</p>
          <div className="text-brand-primary font-medium inline-flex items-center group-hover:underline">
            Explorar <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </Link>
    </AnimatedSection>
  );
} 